(function () {
  'use strict';

  const INSTALLMENTS = 6;
  const PIX_DISCOUNT = 0.10;
  const QUICK_ADD_DELAYS = [80, 220, 520];
  const CARD_BATCH_SIZE = 16;

  const SELECTORS = {
    productScopes: [
      'main product-information',
      'main .product-information',
      'product-information',
      '.product-information'
    ],
    quickAddScopes: [
      '#quick-add-dialog[open]',
      'quick-add-dialog[open]',
      'dialog.quick-add-modal[open]',
      '.quick-add-modal[open]',
      '.quick-add-modal__content',
      '#quick-add-modal-content'
    ],
    cardScopes: [
      '.resource-list__item',
      '[class*="ai-bestsellers-product-card"]',
      '[class*="ai-lancamentos-product-card"]',
      'product-card',
      '.product-card'
    ],
    productLists: [
      '[data-testid="product-list"]',
      '.resource-list',
      'resource-list',
      '[class*="ai-bestsellers"]',
      '[class*="ai-lancamentos"]'
    ].join(','),
    quickAddTriggers: [
      '.quick-add__button',
      'button.quick-add__button',
      'quick-add-component button',
      '[data-testid="quick-add-button"]',
      '[aria-haspopup="dialog"]',
      'button[name="add"]'
    ].join(','),
    variantArea: [
      'variant-picker',
      '.variant-picker',
      '[data-option-value]',
      '[data-selected-variant]',
      'product-information',
      '.product-information',
      '#quick-add-dialog',
      'quick-add-dialog',
      '.quick-add-modal',
      '.quick-add-modal__content'
    ].join(',')
  };

  const timers = new Map();
  const observed = new WeakSet();

  function parseBRL(text) {
    if (!text) return null;

    const normalized = String(text).replace(/\s/g, '');
    const match = normalized.match(/(\d{1,3}(?:\.\d{3})*,\d{2}|\d+,\d{2})/);
    if (!match) return null;

    const value = Number(match[1].replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  function formatBRL(value) {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function paymentHTML(price, mode) {
    const installmentValue = formatBRL(price / INSTALLMENTS);
    const pixValue = formatBRL(price * (1 - PIX_DISCOUNT));
    const isCard = mode === 'card';
    const installmentText = isCard
      ? `<strong>${INSTALLMENTS}x de ${installmentValue}</strong> sem juros`
      : `Em até <strong>${INSTALLMENTS}x de ${installmentValue}</strong> sem juros`;

    return `
      <div class="frenesi-installments">${installmentText}</div>
      <div class="frenesi-pix-price"><strong>${pixValue}</strong> no <span>PIX</span></div>
    `;
  }

  function schedule(key, callback, delay) {
    if (timers.has(key)) window.clearTimeout(timers.get(key));
    timers.set(
      key,
      window.setTimeout(function () {
        timers.delete(key);
        callback();
      }, delay || 90)
    );
  }

  function runIdle(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 900 });
      return;
    }
    window.setTimeout(callback, 1);
  }

  function isOwnPaymentElement(element) {
    return !!element.closest('.frenesi-payment-info, .frenesi-payment-info-card');
  }

  function isVisiblePrice(element) {
    if (!element || isOwnPaymentElement(element)) return false;
    if (element.classList.contains('price--hidden') || element.closest('.price--hidden')) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function findPriceInside(scope, mode) {
    if (!scope) return null;

    const selectors = mode === 'card'
      ? [
          'product-price .price:not(.price--hidden)',
          '.price__regular .price:not(.price--hidden)',
          '[class*="price-wrapper"] [class*="price"]:not([class*="compare"]):not(.price--hidden)',
          '.price:not(.price--hidden)',
          '[class*="-price-"]:not([class*="compare"]):not([class*="wrapper"])'
        ]
      : [
          'product-price .price:not(.price--hidden)',
          'product-price span.price:not(.price--hidden)',
          '.price__regular .price:not(.price--hidden)',
          '.price:not(.price--hidden)',
          '[class*="priceContainer"] .price:not(.price--hidden)'
        ];

    for (const selector of selectors) {
      const elements = scope.querySelectorAll(selector);
      for (const element of elements) {
        if (!isVisiblePrice(element)) continue;
        const value = parseBRL(element.textContent);
        if (value) return { element, value };
      }
    }

    return null;
  }

  function getPriceAnchor(priceElement) {
    return (
      priceElement.closest('product-price') ||
      priceElement.closest('.price__regular') ||
      priceElement.closest('[class*="price-wrapper"]') ||
      priceElement.closest('[data-block-id]') ||
      priceElement.parentElement
    );
  }

  function upsertBox(scope, priceElement, price, mode) {
    const className = mode === 'card' ? 'frenesi-payment-info-card' : 'frenesi-payment-info';
    const roundedPrice = price.toFixed(2);
    let box = scope.querySelector(`.${className}`);

    if (!box) {
      box = document.createElement('div');
      box.className = className;
      box.setAttribute('aria-live', 'polite');

      const anchor = getPriceAnchor(priceElement);
      if (!anchor) return;
      anchor.insertAdjacentElement('afterend', box);
    }

    if (box.dataset.price === roundedPrice && box.dataset.mode === mode) return;

    box.dataset.price = roundedPrice;
    box.dataset.mode = mode;
    box.innerHTML = paymentHTML(price, mode);
  }

  function uniqueScopes(selectors, reject) {
    const scopes = [];
    const seen = new WeakSet();

    selectors.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (scope) {
        if (!scope || seen.has(scope)) return;
        if (reject && reject(scope)) return;
        seen.add(scope);
        scopes.push(scope);
      });
    });

    return scopes;
  }

  function getMainProductScopes() {
    return uniqueScopes(SELECTORS.productScopes, function (scope) {
      return !!scope.closest('#quick-add-dialog, quick-add-dialog, dialog.quick-add-modal, .quick-add-modal, .quick-add-modal__content, #quick-add-modal-content');
    });
  }

  function getQuickAddScopes() {
    return uniqueScopes(SELECTORS.quickAddScopes);
  }

  function getCardScopes(container) {
    const root = container || document;
    const scopes = [];
    const seen = new WeakSet();

    SELECTORS.cardScopes.forEach(function (selector) {
      root.querySelectorAll(selector).forEach(function (scope) {
        if (!scope || seen.has(scope)) return;
        if (scope.closest('product-information, .product-information, #quick-add-dialog, quick-add-dialog, dialog.quick-add-modal, .quick-add-modal, .quick-add-modal__content, #quick-add-modal-content')) return;
        seen.add(scope);
        scopes.push(scope);
      });
    });

    return scopes;
  }

  function updateScope(scope, mode) {
    const result = findPriceInside(scope, mode);
    if (!result) return;
    upsertBox(scope, result.element, result.value, mode);
  }

  function updateProductPages() {
    getMainProductScopes().forEach(function (scope) {
      updateScope(scope, 'product');
      observeScopePrice(scope, 'product');
    });
  }

  function updateQuickAdd() {
    getQuickAddScopes().forEach(function (scope) {
      updateScope(scope, 'product');
      observeScopePrice(scope, 'product');
    });
  }

  function updateProductCards(container) {
    const scopes = getCardScopes(container);
    let index = 0;

    function processBatch() {
      const end = Math.min(index + CARD_BATCH_SIZE, scopes.length);
      for (; index < end; index += 1) {
        updateScope(scopes[index], 'card');
      }
      if (index < scopes.length) runIdle(processBatch);
    }

    runIdle(processBatch);
  }

  function observeScopePrice(scope, mode) {
    if (!scope || observed.has(scope)) return;
    observed.add(scope);

    const observer = new MutationObserver(function (mutations) {
      const relevant = mutations.some(function (mutation) {
        const target = mutation.target;
        if (!(target instanceof Element) && target.nodeType !== Node.TEXT_NODE) return false;
        const element = target.nodeType === Node.TEXT_NODE ? target.parentElement : target;
        return !!element && !isOwnPaymentElement(element) && !!element.closest('product-price, .price__regular, [class*="priceContainer"], .price');
      });

      if (relevant) {
        schedule(mode === 'card' ? 'cards' : 'product', function () {
          updateScope(scope, mode);
        }, 80);
      }
    });

    observer.observe(scope, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  function observeProductLists() {
    document.querySelectorAll(SELECTORS.productLists).forEach(function (list) {
      if (observed.has(list)) return;
      observed.add(list);

      const observer = new MutationObserver(function (mutations) {
        const hasNewProducts = mutations.some(function (mutation) {
          return Array.from(mutation.addedNodes).some(function (node) {
            return node instanceof Element && (
              node.matches?.('.resource-list__item, product-card, .product-card') ||
              node.querySelector?.('.resource-list__item, product-card, .product-card')
            );
          });
        });

        if (hasNewProducts) {
          schedule('cards-list', function () {
            updateProductCards(list);
          }, 120);
        }
      });

      observer.observe(list, { childList: true, subtree: true });
    });
  }

  function observeQuickAddDialogs() {
    const dialogs = uniqueScopes(['#quick-add-dialog', 'quick-add-dialog', 'dialog.quick-add-modal', '.quick-add-modal', '#quick-add-modal-content', '.quick-add-modal__content']);

    dialogs.forEach(function (dialog) {
      if (observed.has(dialog)) return;
      observed.add(dialog);

      const observer = new MutationObserver(function () {
        schedule('quick-add', updateQuickAdd, 80);
      });

      observer.observe(dialog, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['open', 'class', 'style']
      });
    });
  }

  function renderAllPaymentInfo() {
    updateProductPages();
    updateProductCards();
    updateQuickAdd();
    observeProductLists();
    observeQuickAddDialogs();
  }

  function scheduleCardRenderBursts(container) {
    [80, 250, 650, 1200].forEach(function (delay) {
      window.setTimeout(function () {
        updateProductCards(container || document);
      }, delay);
    });
  }

  function observeGlobalProductInsertions() {
    const target = document.querySelector('main') || document.body;
    if (!target || target.dataset.frenesiPaymentGlobalObserver === 'true') return;
    target.dataset.frenesiPaymentGlobalObserver = 'true';

    const observer = new MutationObserver(function (mutations) {
      let container = null;
      const hasNewProductContent = mutations.some(function (mutation) {
        return Array.from(mutation.addedNodes).some(function (node) {
          if (!(node instanceof Element)) return false;

          const isProductContent = node.matches?.('.resource-list__item, product-card, .product-card, [data-product-id], [class*="product-card"]') ||
            node.querySelector?.('.resource-list__item, product-card, .product-card, [data-product-id], [class*="product-card"]');

          if (isProductContent && !container) {
            container = node.closest?.('[data-testid="product-list"], .resource-list, resource-list, main') || node.parentElement || document;
          }

          return Boolean(isProductContent);
        });
      });

      if (hasNewProductContent) {
        schedule('global-cards-list', function () {
          updateProductCards(container || document);
          observeProductLists();
          scheduleCardRenderBursts(container || document);
        }, 180);
      }
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  function scheduleQuickAddBursts() {
    QUICK_ADD_DELAYS.forEach(function (delay) {
      window.setTimeout(updateQuickAdd, delay);
    });
  }

  function bindEvents() {
    document.addEventListener('change', function (event) {
      if (!event.target.closest(SELECTORS.variantArea)) return;
      schedule('variant-change', function () {
        updateProductPages();
        updateQuickAdd();
      }, 120);
    }, true);

    document.addEventListener('click', function (event) {
      if (event.target.closest(SELECTORS.quickAddTriggers)) {
        scheduleQuickAddBursts();
        return;
      }

      if (event.target.closest(SELECTORS.variantArea)) {
        schedule('variant-click', function () {
          updateProductPages();
          updateQuickAdd();
        }, 180);
      }
    }, true);

    window.addEventListener('pageshow', function () {
      renderAllPaymentInfo();
      scheduleQuickAddBursts();
    });

    document.addEventListener('shopify:section:load', function () {
      schedule('shopify-section-prices', renderAllPaymentInfo, 160);
    });

    document.addEventListener('paginated-list:loaded', function () {
      schedule('paginated-list-prices', renderAllPaymentInfo, 160);
    });

    document.addEventListener('facets:loaded', function () {
      schedule('facets-prices', renderAllPaymentInfo, 160);
    });

    window.addEventListener('scroll', function () {
      schedule('scroll-prices', function () {
        updateProductCards();
      }, 350);
    }, { passive: true });
  }

  function init() {
    renderAllPaymentInfo();
    observeGlobalProductInsertions();
    bindEvents();
    scheduleQuickAddBursts();

    window.FrenesiRenderProductPrices = renderAllPaymentInfo;

    // Fallbacks para componentes que hidratam depois do carregamento inicial
    // e para produtos inseridos por scroll infinito.
    // Reduzido de 3 para 2 execuções (400ms cobre hidratação inicial;
    // 1600ms cobre componentes tardios). O intermediário de 900ms era redundante
    // com os MutationObservers já ativos.
    [400, 1600].forEach(function (delay) {
      window.setTimeout(renderAllPaymentInfo, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
